import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  GoogleMap,
  useLoadScript,
  Autocomplete,
} from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import env from '../../../env';
import markerIcon from '../../../assets/images/marker.svg';
import NationalAddressForm, { AddressDetails } from './national-address-form';

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
};
const LIBRARIES: ('places')[] = ['places'];

type Coordinates = { lat: number; lng: number };
interface MapProps {
  onCoordinatesChange: (lat: number, lng: number) => void;
  addressDetails: AddressDetails;
  onAddressChange: (field: keyof AddressDetails, value: string) => void;
  errors?: any;
}

function MyLocationIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' className='w-6 h-6'>
      <g id='SVGRepo_bgCarrier' strokeWidth='0' />
      <g id='SVGRepo_tracerCarrier' strokeLinecap='round' strokeLinejoin='round' />
      <g id='SVGRepo_iconCarrier'><path fillRule='evenodd' clipRule='evenodd' d='M11 2a1 1 0 0 1 2 0v2.062A8.004 8.004 0 0 1 19.938 11H22a1 1 0 0 1 0 2h-2.062A8.004 8.004 0 0 1 13 19.938V22a1 1 0 0 1-2 0v-2.062A8.004 8.004 0 0 1 4.062 13H2a1 1 0 0 1 0-2h2.062A8.004 8.004 0 0 1 11 4.062V2zm7 10a6 6 0 1 0-12 0 6 6 0 0 0 12 0zm-3 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0z' fill='#000000' /></g>
    </svg>
  );
}

function Map({
  onCoordinatesChange, addressDetails, onAddressChange, errors,
}: MapProps) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });
  const [mapCenterView, setMapCenterView] = useState<Coordinates | null>(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState<Coordinates | null>(null);
  const [zoom, setZoom] = useState<number>(16);
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();

  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);

  useEffect(() => {
    onCoordinatesChangeRef.current = onCoordinatesChange;
  }, [onCoordinatesChange]);

  useEffect(() => {
    const setDefaultLocation = () => {
      const defaultLoc: Coordinates = { lat: 24.7136, lng: 46.6753 };
      setMapCenterView(defaultLoc);
      setSelectedCoordinates(defaultLoc);
      setZoom(16);
      if (onCoordinatesChangeRef.current) {
        onCoordinatesChangeRef.current(defaultLoc.lat, defaultLoc.lng);
      }
    };

    if (!mapCenterView) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation: Coordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setMapCenterView(userLocation);
            setSelectedCoordinates(userLocation);
            setZoom(16);
            if (onCoordinatesChangeRef.current) {
              onCoordinatesChangeRef.current(userLocation.lat, userLocation.lng);
            }
          },
          () => {
            setDefaultLocation();
          },
        );
      } else {
        setDefaultLocation();
      }
    }
  }, []);

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();

      setMapCenterView({
        lat: newLat,
        lng: newLng,
      });

      setSelectedCoordinates({
        lat: newLat,
        lng: newLng,
      });
      if (onCoordinatesChange) {
        onCoordinatesChange(newLat, newLng);
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          if (inputRef.current) {
            inputRef.current.value = results[0].formatted_address;
          }
        }
      });
    }
  }, [onCoordinatesChange]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    const newMapCenter = mapRef.current?.getCenter();
    if (newMapCenter) {
      const newLat = newMapCenter.lat();
      const newLng = newMapCenter.lng();

      setMapCenterView({
        lat: newLat,
        lng: newLng,
      });
      setSelectedCoordinates({
        lat: newLat,
        lng: newLng,
      });
      if (onCoordinatesChange) {
        onCoordinatesChange(newLat, newLng);
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          if (inputRef.current) {
            inputRef.current.value = results[0].formatted_address;
          }
        }
      });
    }
  }, [onCoordinatesChange]);

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry?.location) {
      const location: Coordinates = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setMapCenterView(location);
      setSelectedCoordinates(location);
      setZoom(17);
      if (onCoordinatesChange) {
        onCoordinatesChange(location.lat, location.lng);
      }
      if (inputRef.current && place.formatted_address) {
        inputRef.current.value = place.formatted_address;
      }
    }
  }, [onCoordinatesChange]);

  const handleMapZoomChanged = useCallback(() => {
    if (mapRef.current) {
      const newZoom = mapRef.current.getZoom();
      if (newZoom && newZoom !== zoom) {
        setZoom(newZoom);
      }
    }
  }, [zoom]);

  const handleZoomIn = useCallback(() => {
    if (selectedCoordinates) {
      setMapCenterView(selectedCoordinates);
    }
    setZoom((prev) => Math.min(prev + 1, 22));
  }, [selectedCoordinates]);

  const handleZoomOut = useCallback(() => {
    if (selectedCoordinates) {
      setMapCenterView(selectedCoordinates);
    }
    setZoom((prev) => Math.max(prev - 1, 1));
  }, [selectedCoordinates]);

  const handleGoToMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation: Coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setMapCenterView(userLocation);
          setSelectedCoordinates(userLocation);
          setZoom(17);
          if (onCoordinatesChangeRef.current) {
            onCoordinatesChangeRef.current(userLocation.lat, userLocation.lng);
          }
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        },
        () => {
          // Error handling for geolocation failure (e.g., user denied permission)
          // UI feedback for this case can be added here if needed, without using alert.
        },
      );
    } else {
      // Geolocation not supported by the browser.
      // UI feedback can be added here if needed.
    }
  }, []);

  if (!isLoaded || !mapCenterView) {
    return (
      <div className='border-b border-gray-200'>
        <h2 className='text-lg font-medium text-black mb-4'>{t('checkout.deliveryAddress')}</h2>
        <div className='relative mt-4'>
          <div className='mt-4 animate-pulse bg-gray-300 rounded h-96 w-full' />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className='flex flex-col md:flex-row gap-4'>
        <div className='flex-1'>
          <div className='mb-5'>
            <label className='block text-sm font-medium leading-6 text-black'>
              {t('checkout.deliveryAddress')}
            </label>
            <div className='relative mt-2'>
              <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
              </div>
              <Autocomplete
                onLoad={(autocomplete) => {
                  autocompleteRef.current = autocomplete;
                }}
                onPlaceChanged={handlePlaceChanged}
                options={{
                  componentRestrictions: { country: 'SA' },
                  fields: ['geometry', 'formatted_address', 'name'],
                }}
              >
                <input
                  type='text'
                  placeholder={t('checkout.searchOrClickMap')}
                  className='block w-full rounded-md border-0 py-2.5 pl-10 pr-4 text-black shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6'
                  ref={inputRef}
                />
              </Autocomplete>
            </div>
          </div>
        </div>
        <div className='flex-1'>
          <NationalAddressForm
            addressDetails={addressDetails}
            onChange={onAddressChange}
            errors={errors}
            hideTitle
          />
        </div>
      </div>

      <div className='relative'>
        <GoogleMap
          id='map'
          mapContainerClassName='h-96 w-full rounded-md outline-none'
          zoom={zoom}
          center={mapCenterView}
          options={mapOptions}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onClick={handleMapClick}
          onZoomChanged={handleMapZoomChanged}
        />
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-[calc(100%)] z-10 pointer-events-none pb-1 transition-transform duration-300 ${isDragging ? '-translate-y-[calc(100%+10px)]' : ''}`}>
          <img
            src={markerIcon}
            alt='Map Marker'
            width={30}
            height={30}
            className={`drop-shadow-md ${!isDragging ? 'animate-bounce-short' : ''}`}
          />
        </div>

        {/* Custom Map Controls */}
        <div className='absolute bottom-6 right-4 z-10 flex flex-col gap-3'>
          <button
            type='button'
            onClick={handleGoToMyLocation}
            className='bg-white p-3 rounded-full shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200'
            aria-label={t('checkout.myLocation') ?? 'My Location'}
          >
            <MyLocationIcon />
          </button>

          <div className='flex flex-col bg-white rounded-lg shadow-lg overflow-hidden divide-y divide-gray-100'>
            <button
              type='button'
              onClick={handleZoomIn}
              className='p-3 hover:bg-gray-50 focus:outline-none transition-colors duration-200'
              aria-label={t('checkout.zoomIn') ?? 'Zoom In'}
            >
              <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6v6m0 0v6m0-6h6m-6 0H6' />
              </svg>
            </button>
            <button
              type='button'
              onClick={handleZoomOut}
              className='p-3 hover:bg-gray-50 focus:outline-none transition-colors duration-200'
              aria-label={t('checkout.zoomOut') ?? 'Zoom Out'}
            >
              <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 12H4' />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Map;
